import { ANONYMOUS_API, refreshToken, TOKENIZED_API, updateTokenizedApi } from '../../../utils/api';
import {
  setAccountCreationErrors,
  setAccountCreationSuccessful,
  setCustomerEmailFound,
  setCustomerEmailOnFile,
  setCustomerId,
  setErrors,
  setRegisterSuccessful,
  setRegistrationFields,
  setRegistrationToken,
  setVerifyCodeSuccessful,
} from '../login/loginReducer';
import { ga4Event } from '../../../utils/google-analytics-4';
import { isEmail } from '../../../utils/validators/generalValidators';
import { setLoading } from '../enable-mfa/mfaReducer';

export const getRegistrationFields = (notify) => {
  return (dispatch) => {
    // get sessions first
    return ANONYMOUS_API.post('clients/serviceportal/sessions')
      .then((response) => {
        const jwtToken = response.data.jwtToken;
        updateTokenizedApi(jwtToken);
        TOKENIZED_API.get(
          'coderefs/UWInterfaces/registration-criteria/validation-items/serviceportal',
        ).then((response) => {
          dispatch(setRegistrationFields(response.data.options));
        });
      })
      .catch((err) => {
        notify(
          `We were unable to retrieve registration fields at this time. Please contact your Agent.`,
          {
            variant: 'error',
            anchorOrigin: {
              horizontal: 'center',
              vertical: 'top',
            },
          },
        );
        dispatch(setErrors(err));
      });
  };
};

export const verifyConfirmationCode = (
  userId,
  verificationCode,
  registrationToken,
  setVerifyCodeButtonSubmitting,
  notify,
) => {
  return (dispatch) => {
    dispatch(setLoading(true));
    return TOKENIZED_API.post(`/customerRegistrations/${userId}/verifyEmailCode`, {
      passCode: verificationCode,
      registrationToken: registrationToken,
    })
      .then((response) => {
        if (response.status === 200) {
          dispatch(setVerifyCodeSuccessful(true));
        }
      })
      .catch(() => {
        notify(
          'We were unable to verify your code. Please check if the code is correct or request to resend code.',
          {
            variant: 'error',
            anchorOrigin: {
              horizontal: 'center',
              vertical: 'top',
            },
          },
        );
      })
      .finally(() => setVerifyCodeButtonSubmitting(false));
  };
};

export const updateUserCredentials = (
  userId,
  username,
  password,
  registrationToken,
  isConditionsAccepted,
  email,
  setCreateAccountButtonSubmitting,
  notify,
) => {
  return (dispatch) => {
    dispatch(setLoading(true));
    return TOKENIZED_API.post(`/customerRegistrations/${userId}`, {
      userName: username,
      password: password,
      registrationToken: registrationToken,
      termsAccepted: isConditionsAccepted,
      email: email,
    })
      .then((response) => {
        if (response.status === 200) {
          notify('Success. Your account has been created. You may sign in now.', {
            variant: 'success',
            anchorOrigin: {
              horizontal: 'center',
              vertical: 'top',
            },
          });
          dispatch(setAccountCreationSuccessful(true));
        }
      })
      .catch(() => {
        setCreateAccountButtonSubmitting(false);
        notify('We were unable to create your account at this time.', {
          variant: 'error',
          anchorOrigin: {
            horizontal: 'center',
            vertical: 'top',
          },
        });
      })
      .finally(() => {
        dispatch(setLoading(false));
        setCreateAccountButtonSubmitting(false);
      });
  };
};

export const registerCustomer = (payload, notify, setOktaUserId, verifyFlow) => {
  return async (dispatch) => {
    return TOKENIZED_API.post('customerRegistrations', payload)
      .then((response) => {
        if (response.status === 201) {
          // GA4 event for registration
          ga4Event({ category: 'account-registration', action: 'account_registration_complete' });
          !verifyFlow &&
            notify('Success. Your account has been created. You may sign in now.', {
              variant: 'success',
              anchorOrigin: {
                horizontal: 'center',
                vertical: 'top',
              },
            });
          setOktaUserId(response.data.oktaUserId);
          dispatch(setRegisterSuccessful(true));
          if (!verifyFlow) {
            dispatch(setAccountCreationSuccessful(true));
          }
        }
      })
      .catch((err) => {
        // GA4 event for registration
        ga4Event({ category: 'account-registration', action: 'account_registration_failed' });

        // use api error message for 4xx, for others use generic.
        const errorMsg =
          err.response.status > 399 && err.response.status < 500 && err.details
            ? err.details
            : `We were unable to create your account at this time. Please contact an agent for further assistance.`;
        notify(errorMsg, {
          variant: 'error',
          anchorOrigin: {
            horizontal: 'center',
            vertical: 'top',
          },
        });
        dispatch(setAccountCreationErrors(errorMsg));
      });
  };
};

export const verifyRegistrationFields = (
  payload,
  notify,
  isAnonReportClaimsFlow,
  registerCustomerPayload,
  setOktaUserId,
  setRegisterButtonSubmitting,
) => {
  return async (dispatch) => {
    dispatch(setAccountCreationErrors(null));
    dispatch(setCustomerEmailFound(false));
    return TOKENIZED_API.post('customerRegistrations/verificationRequest', payload)
      .then((response) => {
        if (!isAnonReportClaimsFlow) {
          const email = response.data.emailAddr;
          dispatch(setCustomerEmailFound(isEmail(email)));
          dispatch(setCustomerEmailOnFile(email));
        }
        dispatch(setRegistrationToken(response.data.registrationToken));
        dispatch(setCustomerId(response.data.customerId));
        if (registerCustomerPayload) {
          registerCustomerPayload['registrationToken'] = response.data.registrationToken;
          dispatch(registerCustomer(registerCustomerPayload, notify, setOktaUserId, true)); //Only time to run this from here is if verify flow
        }
      })
      .catch(() => {
        notify(
          `We were unable to locate your account. Please contact an agent for further assistance.`,
          {
            variant: 'error',
            anchorOrigin: {
              horizontal: 'center',
              vertical: 'top',
            },
          },
        );
        dispatch(setErrors('Unable To Verify Account'));
        if (setRegisterButtonSubmitting) {
          setRegisterButtonSubmitting(false);
        }
      });
  };
};

export const getCustomersWithCrossLinkToken = (registrationToken, notify) => {
  return async (dispatch) => {
    await refreshToken();
    return TOKENIZED_API.post('customerRegistrations/verificationRequest', { registrationToken })
      .then((response) => {
        const email = response.data.emailAddr;
        dispatch(setCustomerEmailFound(isEmail(email)));
        dispatch(setCustomerEmailOnFile(email));
        dispatch(setRegistrationToken(response.data.registrationToken));
        dispatch(setCustomerId(response.data.customerId));
      })
      .catch(() => {
        const errorMsg = `We were unable to create your account at this time. Please contact an agent for further assistance.}`;
        notify(errorMsg, {
          variant: 'error',
          anchorOrigin: {
            horizontal: 'center',
            vertical: 'top',
          },
        });
        dispatch(setAccountCreationErrors(errorMsg));
      });
  };
};
